import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, api } from '../../api/client';
import { Wordmark } from '../../layout/AppShell';
import { Alert } from '../../ui/feedback';
import { usePageTitle } from '../../ui/misc';

const ERRORS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled at your identity provider.',
  expired_attempt: 'The sign-in attempt took too long. Start again.',
  invalid_response:
    'Sign-in failed — your identity provider returned a response that could not be verified. Try again, or contact an admin if this continues.',
  provider_error:
    'Sign-in failed — your identity provider returned an error. Try again, or contact an admin if this continues.',
};

/** IDN-01/05: invite-only OIDC sign-in with failure and session-expiry feedback. */
export function SignInPage() {
  usePageTitle('Sign in');
  const [params] = useSearchParams();
  const error = params.get('error');
  const returnTo = params.get('returnTo');
  const href = `/api/v1/auth/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`;
  return (
    <div className="auth-layout">
      <div className="auth-card">
        <Wordmark size={36} />
        <div className="stack sm">
          <h1 className="page-title">Sign in</h1>
          <p className="muted">
            Access is invite-only. Sign in with your organization's identity provider. If you received an
            invitation, open its link first.
          </p>
        </div>
        {error && <Alert tone="error">{ERRORS[error] ?? ERRORS.provider_error}</Alert>}
        {params.get('expired') && (
          <Alert tone="warning">
            Your session expired. Sign in again
            {returnTo ? (
              <>
                {' '}
                to return to <span className="mono">{returnTo}</span>
              </>
            ) : (
              ''
            )}
            .
          </Alert>
        )}
        {params.get('signedOut') && <Alert tone="info">You signed out.</Alert>}
        <a className="btn btn-primary block" href={href}>
          Continue with single sign-on
        </a>
        <div className="small muted" style={{ borderTop: '1px solid var(--color-hairline)', paddingTop: 16 }}>
          No invitation? Ask an admin in your organization to invite you.
        </div>
        <DevSignIn returnTo={returnTo} />
      </div>
    </div>
  );
}

/** Shown only when the server runs with DEV_AUTH_ENABLED (never in production builds of the server). */
function DevSignIn({ returnTo }: { returnTo: string | null }) {
  const q = useQuery({
    queryKey: ['dev-login'],
    queryFn: () => api.get<{ enabled: boolean; emails: string[] }>('/auth/dev-login'),
    retry: false,
  });
  const [error, setError] = useState<string | null>(null);
  if (!q.data?.enabled) return null;
  const signIn = async (email: string) => {
    setError(null);
    try {
      const r = await api.post<{ returnTo: string }>('/auth/dev-login', {
        email,
        returnTo: returnTo ?? undefined,
      });
      window.location.assign(r.returnTo);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Development sign-in failed.');
    }
  };
  return (
    <div className="stack sm" style={{ borderTop: '1px dashed var(--color-accent-amber)', paddingTop: 16 }}>
      <Alert tone="warning" title="Development sign-in">
        The identity provider is bypassed for these admin accounts. This is disabled in production.
      </Alert>
      {q.data.emails.map((email) => (
        <button key={email} type="button" className="btn btn-secondary block" onClick={() => signIn(email)}>
          Sign in as {email}
        </button>
      ))}
      {error && <Alert tone="error">{error}</Alert>}
    </div>
  );
}
